import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import { AuthCodeInput, Button, ButtonRetry, Modal, ModalContent, ModalHeader, ModalText, ModalTitle } from 'components';
import { HTTPStatusCode } from 'config/const';
import { theme, toRem } from 'design';
import Form from 'screens/Error/Form';
import { clearRequest, selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { mfaResendRequest, mfaVerifyRequest } from 'store/App';
import * as ActionTypes from 'store/App/types';
import { IStore } from 'store/types';
import { IApiErrorPayloadWithCode } from 'types';

const StyledModalContent = styled(ModalContent)`
  label {
    margin-bottom: ${toRem(12)};
    font-weight: ${theme.font.weight.medium};
    font-size: ${toRem(14)};
  }
`;

const StyledModalText = styled(ModalText)`
  width: ${toRem(390)};
  font-size: ${toRem(14)};
`;

const StyledButton = styled(Button)`
  margin-top: ${toRem(36)};
  width: 52%;
`;

const StyledButtonRetry = styled(ButtonRetry)`
  margin-top: ${toRem(22)};
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(14)};
`;

interface IAuthStepUpVerificationProps {
  authId: string;
  phoneLast4: string;
  subtitle: string;
  backLabel?: string;
  onSuccess?: () => void;
  onClose?: () => void;
}

const AuthStepUpVerification: React.FC<IAuthStepUpVerificationProps> = ({ authId, phoneLast4, subtitle, backLabel = 'Back', onSuccess, onClose }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const apiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, ActionTypes.App.MFA_VERIFY_REQUEST)) as IApiErrorPayloadWithCode;
  const isResendLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, ActionTypes.App.MFA_RESEND_REQUEST));

  const handleOnClose = () => {
    dispatch(clearRequest(ActionTypes.App.MFA_VERIFY_REQUEST));
    onClose();
  };

  const validationSchema = Yup.object().shape({
    code: Yup.string().required('A 6 digit code must be entered').length(6, 'A 6 digit code must be entered'),
  });

  const { handleSubmit, dirty, isValid, setFieldValue } = useFormik({
    initialValues: { code: '' },
    onSubmit: (values: { code: string }) => {
      onSubmit(values.code);
    },
    validateOnChange: true,
    validationSchema,
  });

  const onSubmit = (verificationCode: string) => {
    setIsLoading(true);
    dispatch(mfaVerifyRequest(authId, verificationCode, onSuccess));
  };

  const resendCode = () => {
    dispatch(mfaResendRequest(authId));
  };

  return (
    <Modal maxWidth={560} isActive onClose={handleOnClose} dataCy="step-up-modal">
      <ModalHeader>
        <ModalTitle>Authenticate Your Account</ModalTitle>
        <StyledModalText marginTop={10}>
          {subtitle} Enter the code sent to your mobile number:
          <br />
          (•••) •••-{phoneLast4}
        </StyledModalText>
      </ModalHeader>
      <StyledModalContent>
        <Form onSubmit={handleSubmit} dataCy="form">
          <AuthCodeInput
            inputLabel="Authentication Code"
            errorMessages={apiErrors?.statusCode === HTTPStatusCode.UNAUTHORIZED ? ['The validation code is invalid.'] : apiErrors?.errorMessages}
            onChange={(val) => setFieldValue('code', val)}
          />
          {apiErrors?.errorMessages ? (
            <StyledButton label={backLabel} appearance="white" onClick={handleOnClose} data-cy="back-button" />
          ) : (
            <>
              <StyledButton data-cy="submit-button" type="submit" label="Confirm" disabled={isLoading || !(isValid && dirty)} isProcessing={isLoading} />
              <StyledButtonRetry dataCy="resend-verification-code" label="Resend confirmation code" onClick={resendCode} disabled={isLoading || isResendLoading} isLoading={isResendLoading} />
            </>
          )}
        </Form>
      </StyledModalContent>
    </Modal>
  );
};

export default AuthStepUpVerification;
